import { localeAlternates } from "@repo/shared/i18n";

import { ROUTE_BASE } from "./game";
import { LOCALE_BASE, ROUTER_BASENAME } from "./localePath";

/*
  Canonical URL construction.

  Three rules, matching the indexing policy:
    - canonicalUrl()        self-canonical, keeps both prefixes (/fc27/de/players).
    - rootCanonicalUrl()    drops the route prefix, keeps the locale (/de/squad-builder).
    - englishCanonicalUrl() drops both, for English-only pages (news).

  alternateLinks() renders the hreflang set beside either kind of canonical
  (DEV-3684): a root canonical gets root alternates, an edition canonical gets
  edition alternates, derived from the canonical string alone.

  Both always strip query strings and hashes. Player pages carry
  ?player-role-id=, evo pages carry ?evolution-path=, and none of those are
  distinct documents — a canonical that includes them splits the page into as
  many URLs as there are filter combinations.
*/
const SITE_ORIGIN = "https://www.easysbc.io";

const stripParams = (path: string) => path.split("?")[0].split("#")[0];

const leadingSlash = (path: string) =>
  path.startsWith("/") ? path : `/${path}`;

/** Drops a trailing slash so /players/ and /players don't both get indexed. */
const stripTrailingSlash = (path: string) =>
  path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;

const build = (base: string, path: string) =>
  `${SITE_ORIGIN}${stripTrailingSlash(`${base}${leadingSlash(stripParams(path))}`)}`;

/**
 * Self-referencing canonical for this build. Pass a router-relative path to
 * pin it, or omit to use the current location. `window.location.pathname`
 * already carries both prefixes, so it is used as-is.
 */
export const canonicalUrl = (path?: string): string =>
  path === undefined
    ? build("", window.location.pathname)
    : build(ROUTER_BASENAME, path);

const stripPrefix = (pathname: string) =>
  ROUTER_BASENAME && pathname.startsWith(ROUTER_BASENAME)
    ? pathname.slice(ROUTER_BASENAME.length) || "/"
    : pathname;

/**
 * Canonical pointing at the root deploy, for pages that are byte-identical
 * across builds. Keeps the locale so hreflang stays valid.
 */
export const rootCanonicalUrl = (path?: string): string =>
  build(LOCALE_BASE, path ?? stripPrefix(window.location.pathname));

export const englishCanonicalUrl = (path: string): string => build("", path);

/**
 * hreflang links for `canonical`, for the same <Helmet> as the canonical tag.
 * Elements rather than a component: Helmet only accepts plain head elements.
 */
export const alternateLinks = (canonical: string) => {
  const path = canonical.slice(SITE_ORIGIN.length);
  const editionBase =
    ROUTE_BASE && path.startsWith(ROUTE_BASE) ? ROUTE_BASE : "";
  return localeAlternates(canonical, editionBase).map(({ hreflang, href }) => (
    <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
  ));
};
